import type { AnalysisState, ScalarValue, TimeGrain } from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import { DiagnosticBag, type AnalysisDiagnostic } from './diagnostics.js';
import type { PlannedHierarchyLevel } from './query-plan.js';

/** Dimension del analisis tras aplicar el drilldown. */
export interface EffectiveDimension {
  readonly key: string;
  readonly dimensionId: string;
  readonly timeGrain: TimeGrain | null;
  readonly label: string | null;
  readonly origin: 'state' | 'drilldown';
  readonly hierarchy: PlannedHierarchyLevel | null;
  /** Ruta en el Analysis State de la dimension original. */
  readonly path: string;
}

/** Valor elegido en un nivel superior de la jerarquia: se convierte en filtro. */
export interface DrilldownPathFilter {
  readonly id: string;
  readonly dimensionId: string;
  readonly value: ScalarValue;
  readonly path: string;
}

export interface DrilldownResolution {
  readonly dimensions: readonly EffectiveDimension[];
  readonly pathFilters: readonly DrilldownPathFilter[];
  readonly diagnostics: readonly AnalysisDiagnostic[];
}

/**
 * Aplica el drilldown: la dimension del analisis que pertenece a la jerarquia se
 * sustituye (en su misma posicion y con la misma `key`) por la del nivel actual, y los
 * valores de los niveles superiores se convierten en filtros.
 *
 * @example
 * resolveDrilldown(state, index).dimensions.map((d) => d.dimensionId); // ['dim.city']
 */
export function resolveDrilldown(
  state: Pick<AnalysisState, 'dimensions' | 'drilldown' | 'interaction'>,
  index: SemanticModelIndex,
): DrilldownResolution {
  const bag = new DiagnosticBag();
  const dimensions: EffectiveDimension[] = state.dimensions.map((dimension, i) => ({
    key: dimension.key,
    dimensionId: dimension.dimensionId,
    timeGrain: dimension.timeGrain ?? null,
    label: dimension.label ?? null,
    origin: 'state',
    hierarchy: hierarchyOf(dimension.dimensionId, index),
    path: `dimensions.${i}`,
  }));
  const pathFilters: DrilldownPathFilter[] = [];

  if (state.drilldown.length > 0 && state.interaction.drillMode === 'none') {
    bag.warning('ignored', 'El drilldown se ignora porque la interaccion lo desactiva.', {
      path: 'interaction.drillMode',
    });
    return { dimensions, pathFilters, diagnostics: bag.items };
  }

  const seenHierarchies = new Set<string>();
  state.drilldown.forEach((drill, i) => {
    const base = `drilldown.${i}`;
    const hierarchy = index.getHierarchy(drill.hierarchyId);
    if (!hierarchy) {
      bag.error('missing-reference', `Jerarquia inexistente "${drill.hierarchyId}".`, {
        path: `${base}.hierarchyId`,
      });
      return;
    }
    if (seenHierarchies.has(hierarchy.id)) {
      bag.error('invalid-state', `La jerarquia "${hierarchy.id}" tiene mas de un drilldown.`, {
        path: `${base}.hierarchyId`,
      });
      return;
    }
    seenHierarchies.add(hierarchy.id);
    const depth = hierarchy.levels.findIndex((level) => level.id === drill.levelId);
    const level = hierarchy.levels[depth];
    if (!level) {
      bag.error('missing-reference', `Nivel inexistente "${drill.levelId}".`, {
        path: `${base}.levelId`,
      });
      return;
    }

    const stepLevels = new Set<string>();
    drill.path.forEach((step, j) => {
      const path = `${base}.path.${j}`;
      const stepDepth = hierarchy.levels.findIndex((candidate) => candidate.id === step.levelId);
      const stepLevel = hierarchy.levels[stepDepth];
      if (!stepLevel) {
        bag.error('missing-reference', `Nivel inexistente "${step.levelId}".`, {
          path: `${path}.levelId`,
        });
        return;
      }
      if (stepDepth >= depth) {
        bag.error(
          'invalid-state',
          `El nivel "${step.levelId}" de la ruta debe ser superior al nivel actual "${level.id}".`,
          { path: `${path}.levelId` },
        );
        return;
      }
      if (stepLevels.has(step.levelId)) {
        bag.error('invalid-state', `El nivel "${step.levelId}" se repite en la ruta.`, {
          path: `${path}.levelId`,
        });
        return;
      }
      stepLevels.add(step.levelId);
      pathFilters.push({
        id: `@drilldown.${i}.${j}`,
        dimensionId: stepLevel.dimensionId,
        value: step.value,
        path,
      });
    });

    const levelDimensions = new Set(hierarchy.levels.map((candidate) => candidate.dimensionId));
    const position = dimensions.findIndex((dimension) =>
      levelDimensions.has(dimension.dimensionId),
    );
    const current = dimensions[position];
    if (!current) {
      bag.warning(
        'ignored',
        `El analisis no incluye ninguna dimension de la jerarquia "${hierarchy.id}"; solo se aplican los filtros de la ruta.`,
        { path: base },
      );
      return;
    }
    const levelRef: PlannedHierarchyLevel = { hierarchyId: hierarchy.id, levelId: level.id, depth };
    dimensions[position] =
      current.dimensionId === level.dimensionId
        ? { ...current, hierarchy: levelRef }
        : {
            key: current.key,
            dimensionId: level.dimensionId,
            timeGrain: null,
            label: null,
            origin: 'drilldown',
            hierarchy: levelRef,
            path: current.path,
          };
  });

  return { dimensions, pathFilters, diagnostics: bag.items };
}

/** Nivel de jerarquia de una dimension: la declarada en la dimension o la primera que la contiene. */
function hierarchyOf(dimensionId: string, index: SemanticModelIndex): PlannedHierarchyLevel | null {
  const declared = index.getDimension(dimensionId)?.hierarchyId;
  const candidates =
    declared === undefined
      ? index.model.hierarchies
      : [
          ...index.model.hierarchies.filter((hierarchy) => hierarchy.id === declared),
          ...index.model.hierarchies.filter((hierarchy) => hierarchy.id !== declared),
        ];
  for (const hierarchy of candidates) {
    const depth = hierarchy.levels.findIndex((level) => level.dimensionId === dimensionId);
    const level = hierarchy.levels[depth];
    if (level) return { hierarchyId: hierarchy.id, levelId: level.id, depth };
  }
  return null;
}
